import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import clsx from "clsx";
import dayjs from "dayjs";
import { useMemo } from "react";
import type { DayOfWeek } from "../Calendar";
import { Calendar } from "../Calendar";
import { useControllableState } from "../utils";

interface DateRangePickerCalendarProps {
  firstDayOfWeek?: DayOfWeek;
  minDay?: dayjs.Dayjs;

  day?: dayjs.Dayjs;
  defaultDay?: dayjs.Dayjs;
  onDayChange?: (day: dayjs.Dayjs) => void;
}

export function DateRangePickerCalendar({
  firstDayOfWeek,
  minDay,
  day: dayProp,
  defaultDay,
  onDayChange: onDayChangeProp,
}: DateRangePickerCalendarProps) {
  const [day, onDayChange] = useControllableState({
    prop: dayProp,
    defaultProp: defaultDay,
    onChange: onDayChangeProp,
  });

  const minimumDay = useMemo(() => {
    if (minDay) {
      return minDay;
    }
    return dayjs();
  }, [minDay]);

  const buttonClassnames = clsx(
    "min-w-[2.5rem]",
    "min-h-[2.5rem]",
    "bg-transparent",
    "inline-flex",
    "items-center",
    "justify-center",
    "p-1",
    "transition-all",
    "duration-100",
    "rounded-xl",
    "hover:bg-interaction"
  );

  return (
    <div>
      <Calendar firstDayOfWeek={firstDayOfWeek} day={day} onDayChange={onDayChange}>
        <div className={clsx("py-2", "px-4", "border-b", "border-b-gray-200")}>
          <div className={clsx("flex", "items-center", "space-x-4")}>
            <div className={clsx("min-w-0", "flex-grow")}>
              <Calendar.Label className="inline-block">
                {({ day }) => (
                  <span className={clsx("text-base", "leading-normal", "font-bold")}>{day.format("YYYY年M月")}</span>
                )}
              </Calendar.Label>
            </div>

            <div className="min-w-0">
              <Calendar.Control>
                <div className={clsx("flex", "items-center", "space-x-1")}>
                  <Calendar.Prev aria-label="前月へ" title="前月" className={buttonClassnames}>
                    <ChevronLeftIcon width="20" height="20" />
                  </Calendar.Prev>
                  <Calendar.Reset aria-label="今月へ" title="今月" className={buttonClassnames}>
                    <CalendarIcon width="20" height="20" />
                  </Calendar.Reset>
                  <Calendar.Next aria-label="翌月へ" title="翌月" className={buttonClassnames}>
                    <ChevronRightIcon width="20" height="20" />
                  </Calendar.Next>
                </div>
              </Calendar.Control>
            </div>
          </div>
        </div>
        <div className={clsx("relative", "pt-3", "px-4", "pb-4")}>
          <Calendar.Month className={clsx("border-collapse", "table-fixed", "block")}>
            <Calendar.Caption />
            <Calendar.Header
              className={clsx(
                "block",
                `[&>_tr]:my-2`,
                `[&>_tr]:flex`,
                `[&>_tr]:flex-nowrap`,
                `[&>_tr_>_th]:min-w-[2.2rem]`,
                `[&>_tr_>_th]:flex-[1_1]`,
                `[&>_tr_>_th]:text-center`,
                `[&>_tr_>_th]:p-0`,
                `[&>_tr_>_th]:text-base`,
                `[&>_tr_>_th]:leading-normal`,
                `[&>_tr_>_th]:font-medium`,
                `[&>_tr_>_th]:text-gray-700`,
                `[&>_tr_>_th]:uppercase`
              )}
            >
              {({ day }) => <Calendar.HeaderDay day={day}>{day}</Calendar.HeaderDay>}
            </Calendar.Header>
            <Calendar.Grid
              className={clsx(
                "block",
                "pt-1",
                `[&>_tr]:my-2`,
                `[&>_tr]:flex`,
                `[&>_tr]:flex-nowrap`,
                `[&>_tr_>_td]:flex-[1_1]`,
                `[&>_tr_>_td]:flex`,
                `[&>_tr_>_td]:items-center`,
                `[&>_tr_>_td]:justify-center`,
                `[&>_tr_>_td]:p-0`,
                `[&>_tr_>_td]:text-center`
              )}
            >
              {({ week }) => (
                <Calendar.Week week={week}>
                  {({ day: dayProp }) => (
                    <Calendar.Day day={dayProp}>
                      {({ day: dayProp }) => {
                        const isToday = day?.format("YYYY-MM-DD") === dayProp.format("YYYY-MM-DD");
                        const allowed = minimumDay.format("YYYY-MM-DD") === dayProp.format("YYYY-MM-DD");
                        const disabled = allowed ? false : minimumDay.isAfter(dayProp) || false;
                        return (
                          <Calendar.DayAction
                            day={dayProp}
                            disabled={disabled}
                            className={clsx(
                              "relative",
                              "min-w-[2rem]",
                              "min-h-[2rem]",
                              "flex",
                              "items-center",
                              "justify-center",
                              "leading-none",
                              "text-[16px]",
                              "rounded",
                              disabled
                                ? [
                                    "cursor-not-allowed",
                                    "text-gray-300",
                                    "before:inset-0",
                                    "before:rounded",
                                    "before:transition-all",
                                    "before:duration-100",
                                    "before:border",
                                    "before:border-transparent",
                                    "hover:before:border-blue-600",
                                    isToday && "hover:before:bg-interaction",
                                  ]
                                : ["cursor-pointer", "text-gray-900"],
                              isToday ? "bg-blue-100" : "bg-transparent"
                            )}
                          >
                            {({ day }) => <Calendar.DayText day={day} />}
                          </Calendar.DayAction>
                        );
                      }}
                    </Calendar.Day>
                  )}
                </Calendar.Week>
              )}
            </Calendar.Grid>
          </Calendar.Month>
        </div>
      </Calendar>
    </div>
  );
}
